import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  ArrowLeft, Package, MapPin, Building2, Shield, Send, 
  CheckCircle, XCircle, Clock, Phone, FileText, Ship,
  Globe, Calendar, DollarSign, Loader2, ChevronLeft, 
  ChevronRight, Heart, Share2, Eye, BadgeCheck, Mail,
  ExternalLink, Star, Users, MessageSquare, Play, Video
} from 'lucide-react';
import { toast } from 'sonner';
import { VerificationStatus, FullyVerifiedBadge, PhoneVerifiedBadge, GSTVerifiedBadge, IECVerifiedBadge } from '@/components/VerificationBadges';
import SEO, { getProductSEO } from '@/components/SEO';

const API = `${window.location.origin}/api`;

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [seller, setSeller] = useState(null);
  const [sellerProducts, setSellerProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('details');
  const [phoneVisible, setPhoneVisible] = useState(false);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  
  // Inquiry state
  const [showInquiryDialog, setShowInquiryDialog] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    quantity: '',
    unit: 'kg',
    message: ''
  });
  
  // Currency state
  const [currencyRates, setCurrencyRates] = useState(null);
  
  // Watchlist state
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [addingToWatchlist, setAddingToWatchlist] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    fetchProduct();
    fetchCurrencyRates();
  }, [productId]);

  const fetchProduct = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
      
      // Fetch seller info
      if (response.data.seller_id) {
        const sellerRes = await axios.get(`${API}/seller/${response.data.seller_id}/profile`);
        setSeller(sellerRes.data.seller);
        setSellerProducts(sellerRes.data.products?.filter(p => p.id !== productId).slice(0, 4) || []);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product details');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrencyRates = async () => {
    try {
      const response = await axios.get(`${API}/currency/rates?base=INR`);
      setCurrencyRates(response.data.rates);
    } catch (error) {
      console.error('Failed to fetch currency rates');
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { 'INR': '₹', 'USD': '$', 'EUR': '€', 'GBP': '£', 'AED': 'د.إ' };
    return symbols[currency] || currency + ' ';
  };

  const handleSendInquiry = async () => {
    if (!user) {
      toast.error('Please login to send an inquiry');
      navigate('/auth');
      return;
    }
    
    if (!inquiryForm.quantity || !inquiryForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/inquiries`, {
        product_id: product.id,
        seller_id: product.seller_id,
        message: `Quantity Required: ${inquiryForm.quantity} ${inquiryForm.unit}\n\n${inquiryForm.message}`,
        quantity: parseInt(inquiryForm.quantity),
        unit: inquiryForm.unit
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Inquiry sent successfully!');
      setShowInquiryDialog(false);
      setInquiryForm({ quantity: '', unit: 'kg', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send inquiry');
    } finally {
      setIsSending(false);
    }
  };

  const isFullyVerified = seller?.phone_verified && seller?.gstin_verified && seller?.iec_status === 'approved';

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">Product Not Found</h2>
              <p className="text-muted-foreground mb-4">The product you're looking for doesn't exist.</p>
              <Button onClick={() => navigate('/marketplace')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Marketplace
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // Generate product-specific SEO
  const productSEO = product ? getProductSEO(product) : { title: 'Product Details', description: 'View product details on Global Vyapar.' };

  return (
    <Layout>
      <SEO {...productSEO} />
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="product-detail-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
          <span className="hover:text-primary cursor-pointer" onClick={() => navigate('/marketplace')}>Marketplace</span>
          <ChevronRight className="h-4 w-4" />
          <span className="hover:text-primary cursor-pointer">{product.category}</span>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground">{product.title}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Product Images & Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Images */}
            <Card>
              <CardContent className="p-4">
                {/* Combine images and videos into media array */}
                {(() => {
                  const images = product.images || [];
                  const videos = product.videos || [];
                  const allMedia = [
                    ...images.map(src => ({ type: 'image', src })),
                    ...videos.map(src => ({ type: 'video', src }))
                  ];
                  const currentMedia = allMedia[currentMediaIndex] || null;
                  
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      {/* Thumbnail Gallery */}
                      <div className="md:col-span-1 flex md:flex-col gap-2 order-2 md:order-1 overflow-y-auto max-h-[400px]">
                        {allMedia.length > 0 ? (
                          allMedia.map((media, idx) => (
                            <div
                              key={idx}
                              className={`relative w-16 h-16 border-2 rounded cursor-pointer overflow-hidden flex-shrink-0 ${
                                currentMediaIndex === idx ? 'border-primary' : 'border-gray-200'
                              }`}
                              onClick={() => setCurrentMediaIndex(idx)}
                            >
                              {media.type === 'image' ? (
                                <img src={media.src} alt={`${product.title} ${idx + 1}`} className="w-full h-full object-cover" />
                              ) : (
                                <>
                                  <video src={media.src} className="w-full h-full object-cover" muted />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                    <Play className="h-4 w-4 text-white" />
                                  </div>
                                </>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="w-16 h-16 border-2 border-gray-200 rounded flex items-center justify-center bg-gray-50">
                            <Package className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      
                      {/* Main Media Display */}
                      <div className="md:col-span-4 order-1 md:order-2">
                        <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center relative">
                          {currentMedia ? (
                            currentMedia.type === 'image' ? (
                              <img 
                                src={currentMedia.src} 
                                alt={product.title}
                                className="max-w-full max-h-full object-contain"
                              />
                            ) : (
                              <video 
                                src={currentMedia.src}
                                controls
                                className="max-w-full max-h-full"
                                poster=""
                              >
                                Your browser does not support video playback.
                              </video>
                            )
                          ) : (
                            <Package className="h-24 w-24 text-gray-400" />
                          )}
                          
                          {/* Media type indicator */}
                          {currentMedia && currentMedia.type === 'video' && (
                            <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                              <Video className="h-3 w-3" />
                              Video
                            </div>
                          )}
                          
                          {/* Navigation arrows if multiple media */}
                          {allMedia.length > 1 && (
                            <>
                              <button
                                onClick={() => setCurrentMediaIndex(prev => prev === 0 ? allMedia.length - 1 : prev - 1)}
                                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow"
                              >
                                <ChevronLeft className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => setCurrentMediaIndex(prev => prev === allMedia.length - 1 ? 0 : prev + 1)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow"
                              >
                                <ChevronRight className="h-5 w-5" />
                              </button>
                            </>
                          )}
                        </div>
                        
                        {/* Media count */}
                        {allMedia.length > 1 && (
                          <div className="text-center mt-2 text-sm text-muted-foreground">
                            {currentMediaIndex + 1} / {allMedia.length}
                            {images.length > 0 && videos.length > 0 && (
                              <span className="ml-2">
                                ({images.length} photos, {videos.length} videos)
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Product Info Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-2xl font-bold mb-2">{product.title}</h1>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary">{product.category}</Badge>
                      {product.origin_country && (
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Globe className="h-3 w-3" />
                          {product.origin_country}
                        </Badge>
                      )}
                      {product.hs_code && (
                        <Badge variant="outline">HS: {product.hs_code}</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon">
                      <Heart className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon">
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Price */}
                <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-4 mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-primary">
                      {getCurrencySymbol(product.currency || 'INR')}{product.price?.toLocaleString()}
                    </span>
                    <span className="text-muted-foreground">/ {product.unit || 'unit'}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Min. Order: {product.min_order || 1} {product.unit || 'units'}
                  </p>
                </div>

                {/* Quick Inquiry Form */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="font-medium mb-3">Interested in this product? Get Latest Price</p>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Enter Quantity" 
                      type="number"
                      value={inquiryForm.quantity}
                      onChange={(e) => setInquiryForm({...inquiryForm, quantity: e.target.value})}
                      className="max-w-[150px]"
                    />
                    <select 
                      className="border rounded-md px-3 py-2 bg-white"
                      value={inquiryForm.unit}
                      onChange={(e) => setInquiryForm({...inquiryForm, unit: e.target.value})}
                    >
                      <option value="kg">Kg</option>
                      <option value="ton">Ton</option>
                      <option value="pieces">Pieces</option>
                      <option value="meters">Meters</option>
                      <option value="liters">Liters</option>
                    </select>
                    <Button onClick={() => setShowInquiryDialog(true)} className="flex-1">
                      <Send className="h-4 w-4 mr-2" />
                      Submit Requirement
                    </Button>
                  </div>
                </div>

                {/* Tabs - Product Details & Company Details */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="details">Product Details</TabsTrigger>
                    <TabsTrigger value="company">Company Details</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="details" className="mt-4">
                    {/* Specifications Table */}
                    {product.specifications && Object.keys(product.specifications).length > 0 && (
                      <div className="mb-6">
                        <h3 className="font-semibold mb-3">Specifications</h3>
                        <div className="border rounded-lg overflow-hidden">
                          {Object.entries(product.specifications).map(([key, value], idx) => (
                            <div key={key} className={`flex ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                              <div className="w-1/3 p-3 font-medium text-gray-600 border-r">{key}</div>
                              <div className="w-2/3 p-3">{value}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    <div>
                      <h3 className="font-semibold mb-3">Product Description</h3>
                      <div className="prose prose-sm max-w-none text-gray-700">
                        <p className="whitespace-pre-wrap">{product.description}</p>
                      </div>
                    </div>

                    {/* Shipping Information */}
                    <div className="mt-6 border-t pt-4">
                      <h3 className="font-semibold mb-3">Shipping Information</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Ships from</span>
                          <p className="font-medium">{seller?.district || product.origin_country || 'India'}</p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Shipping</span>
                          <p className="font-medium">As per delivery location</p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Terms */}
                    {(product.payment_terms?.length > 0 || seller?.preferred_payment_terms?.length > 0) && (
                      <div className="mt-6 border-t pt-4">
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          Accepted Payment Terms
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {(product.payment_terms || seller?.preferred_payment_terms || []).map((term, idx) => (
                            <Badge key={idx} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              {term.replace(/_/g, ' ')}
                            </Badge>
                          ))}
                        </div>
                        {(product.payment_terms_negotiable || seller?.payment_terms_negotiable) && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Terms are negotiable based on order value and relationship
                          </p>
                        )}
                        {product.custom_payment_terms && (
                          <p className="text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                            <span className="font-medium">Custom Terms:</span> {product.custom_payment_terms}
                          </p>
                        )}
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="company" className="mt-4">
                    {/* About the Company */}
                    <div className="space-y-4">
                      <h3 className="font-semibold">About the Company</h3>
                      
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                        {seller?.gstin && (
                          <div>
                            <span className="text-muted-foreground">GSTIN</span>
                            <p className="font-medium">{seller.gstin}</p>
                          </div>
                        )}
                        {seller?.company_type && (
                          <div>
                            <span className="text-muted-foreground">Legal Status</span>
                            <p className="font-medium">{seller.company_type || 'Proprietorship'}</p>
                          </div>
                        )}
                        {seller?.nature_of_business && (
                          <div>
                            <span className="text-muted-foreground">Nature of Business</span>
                            <p className="font-medium">{seller.nature_of_business || 'Exporter'}</p>
                          </div>
                        )}
                        {seller?.created_at && (
                          <div>
                            <span className="text-muted-foreground">Member Since</span>
                            <p className="font-medium">{new Date(seller.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</p>
                          </div>
                        )}
                        {seller?.annual_turnover && (
                          <div>
                            <span className="text-muted-foreground">Annual Turnover</span>
                            <p className="font-medium">{seller.annual_turnover}</p>
                          </div>
                        )}
                        {seller?.country && (
                          <div>
                            <span className="text-muted-foreground">Country</span>
                            <p className="font-medium">{seller.country}</p>
                          </div>
                        )}
                      </div>

                      {seller?.about && (
                        <div className="mt-4">
                          <h4 className="font-medium mb-2">About</h4>
                          <p className="text-sm text-gray-700">{seller.about}</p>
                        </div>
                      )}

                      {seller?.business_address && (
                        <div className="mt-4">
                          <h4 className="font-medium mb-2">Business Address</h4>
                          <p className="text-sm text-gray-700 flex items-start gap-2">
                            <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            {seller.business_address}
                          </p>
                        </div>
                      )}

                      {seller?.website && (
                        <div className="mt-4">
                          <h4 className="font-medium mb-2">Website</h4>
                          <a 
                            href={seller.website.startsWith('http') ? seller.website : `https://${seller.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline flex items-center gap-1"
                          >
                            <Globe className="h-4 w-4" />
                            {seller.website}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* More Products from Seller */}
            {sellerProducts.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">More Products from {seller?.company_name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {sellerProducts.map((p) => (
                      <div 
                        key={p.id}
                        className="border rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => navigate(`/product/${p.id}`)}
                      >
                        <div className="aspect-square bg-gray-100 rounded mb-2 overflow-hidden">
                          {p.images && p.images[0] ? (
                            <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <h4 className="text-sm font-medium line-clamp-2 mb-1">{p.title}</h4>
                        <p className="text-xs text-muted-foreground mb-1">{p.category}</p>
                        <p className="text-sm font-semibold text-primary">
                          {getCurrencySymbol(p.currency || 'INR')}{p.price?.toLocaleString()}
                        </p>
                        <Button variant="link" size="sm" className="p-0 h-auto text-xs">
                          Get Best Price →
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Seller Contact Card */}
          <div className="space-y-4">
            {/* Send Enquiry Card */}
            <Card className="sticky top-20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  {product.images && product.images[0] ? (
                    <img src={product.images[0]} alt={product.title} className="w-16 h-16 rounded object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded bg-gray-100 flex items-center justify-center">
                      <Package className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Send enquiry for</p>
                    <p className="font-semibold line-clamp-2">{product.title}</p>
                  </div>
                </div>
                <Button className="w-full" size="lg" onClick={() => setShowInquiryDialog(true)}>
                  Submit Requirement
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </CardContent>
              
              <Separator />
              
              {/* Seller Contact Details */}
              <CardContent className="p-4">
                <div className="bg-primary text-white px-3 py-2 -mx-4 -mt-4 mb-4">
                  <p className="font-semibold text-sm">Seller Contact Details</p>
                </div>
                
                <div 
                  className="flex items-center gap-3 mb-4 cursor-pointer hover:bg-gray-50 p-2 rounded -mx-2"
                  onClick={() => navigate(`/seller/${seller?.id}`)}
                >
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={seller?.company_logo} />
                    <AvatarFallback>{seller?.company_name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold flex items-center gap-2">
                      {seller?.company_name}
                      <FullyVerifiedBadge 
                        phoneVerified={seller?.phone_verified}
                        gstinVerified={seller?.gstin_verified}
                        iecStatus={seller?.iec_status}
                        size="small"
                        variant="compact"
                      />
                    </p>
                    {seller?.gstin_verified && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        GST Verified
                      </p>
                    )}
                  </div>
                </div>

                {/* Unique Verification Badges */}
                <div className="flex flex-wrap items-center gap-1.5 mb-4">
                  <PhoneVerifiedBadge verified={seller?.phone_verified} size="small" />
                  <GSTVerifiedBadge verified={seller?.gstin_verified} size="small" />
                  <IECVerifiedBadge status={seller?.iec_status} size="small" />
                </div>

                {/* Contact Info */}
                <div className="space-y-3 text-sm">
                  {seller?.district && (
                    <p className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <span>{seller.district}, {seller.country || 'India'}</span>
                    </p>
                  )}
                  
                  {seller?.website && (
                    <a 
                      href={seller.website.startsWith('http') ? seller.website : `https://${seller.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-primary hover:underline"
                    >
                      <Globe className="h-4 w-4" />
                      {seller.website}
                    </a>
                  )}

                  {/* Phone - View Mobile No. (respects phone_public setting) */}
                  {seller?.phone && seller?.phone_public !== false && (
                    phoneVisible ? (
                      <p className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        {seller.phone}
                      </p>
                    ) : (
                      <button 
                        onClick={() => {
                          if (!user) {
                            toast.error('Please login to view contact details');
                            return;
                          }
                          setPhoneVisible(true);
                        }}
                        className="flex items-center gap-2 text-primary hover:underline"
                      >
                        <Phone className="h-4 w-4" />
                        View Mobile No.
                      </button>
                    )
                  )}
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <Button variant="outline" onClick={() => navigate(`/inquiries?seller=${seller?.id}`)}>
                    <Mail className="h-4 w-4 mr-1" />
                    Send Email
                  </Button>
                  <Button onClick={() => setShowInquiryDialog(true)}>
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Contact
                  </Button>
                </div>

                {/* View Profile Button */}
                <Button 
                  variant="ghost" 
                  className="w-full mt-3"
                  onClick={() => navigate(`/seller/${seller?.id}`)}
                >
                  View Full Profile
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Inquiry Dialog */}
        <Dialog open={showInquiryDialog} onOpenChange={setShowInquiryDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Send Inquiry</DialogTitle>
              <DialogDescription>
                Contact {seller?.company_name} about {product.title}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quantity Required</Label>
                  <Input 
                    type="number"
                    placeholder="Enter quantity"
                    value={inquiryForm.quantity}
                    onChange={(e) => setInquiryForm({...inquiryForm, quantity: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Unit</Label>
                  <select 
                    className="w-full border rounded-md px-3 py-2 bg-white"
                    value={inquiryForm.unit}
                    onChange={(e) => setInquiryForm({...inquiryForm, unit: e.target.value})}
                  >
                    <option value="kg">Kg</option>
                    <option value="ton">Ton</option>
                    <option value="pieces">Pieces</option>
                    <option value="meters">Meters</option>
                    <option value="liters">Liters</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Your Requirements</Label>
                <Textarea
                  placeholder="Describe your requirements, specifications, delivery timeline..."
                  value={inquiryForm.message}
                  onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInquiryDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSendInquiry} disabled={isSending}>
                {isSending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" />Send Inquiry</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
